import React, { useState, useEffect } from 'react';
import { GoogleMap, Marker, InfoWindow, useLoadScript } from '@react-google-maps/api';
import { db, rtdb } from '../../firebase';
import { collection, onSnapshot } from 'firebase/firestore';
import { ref, onValue } from 'firebase/database';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '0.5rem',
};

const defaultCenter = {
  lat: -26.1952,  // Johannesburg
  lng: 28.0341,
};

const options = {
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  styles: [
    {
      featureType: 'poi',
      elementType: 'labels',
      stylers: [{ visibility: 'off' }],
    },
  ],
};

// Simple icon URLs (no google.maps.Size needed)
const driverIcon = 'https://maps.google.com/mapfiles/ms/icons/green-dot.png';
const pickupIcon = 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png';

export const LiveMap = () => {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: 'AIzaSyDs6b0EZrLRX3gaBqLy3hB7XkzEwULICkI',
  });
  const [drivers, setDrivers] = useState([]);
  const [activeRides, setActiveRides] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);

  // Real-time drivers from Firestore (driver_states with online=true)
  useEffect(() => {
    const q = collection(db, 'driver_states');
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const driversData = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(driver => driver.online === true && driver.lat && driver.lng);
      setDrivers(driversData);
    });
    return () => unsubscribe();
  }, []);

  // Real-time active rides from Realtime Database
  useEffect(() => {
    const ridesRef = ref(rtdb, 'activeRides');
    const unsubscribe = onValue(ridesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const rides = Object.keys(data).map(rideId => ({ id: rideId, ...data[rideId] }));
        setActiveRides(rides);
      } else {
        setActiveRides([]);
      }
    });
    return () => unsubscribe();
  }, []);

  if (loadError) return <div className="text-red-400 p-4">Error loading maps: {loadError.message}</div>;
  if (!isLoaded) return <div className="text-gray-400 p-4">Loading map...</div>;

  return (
    <div className="w-full h-full relative">
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={12}
        center={defaultCenter}
        options={options}
      >
        {/* Driver markers */}
        {drivers.map(driver => (
          <Marker
            key={driver.id}
            position={{ lat: driver.lat, lng: driver.lng }}
            icon={driverIcon}
            onClick={() => setSelectedDriver(driver)}
          />
        ))}

        {/* Active ride pickup markers */}
        {activeRides.map(ride => (
          ride.pickupLocation && (
            <Marker
              key={ride.id}
              position={{ lat: ride.pickupLocation.latitude, lng: ride.pickupLocation.longitude }}
              icon={pickupIcon}
            />
          )
        ))}

        {/* InfoWindow for selected driver */}
        {selectedDriver && (
          <InfoWindow
            position={{ lat: selectedDriver.lat, lng: selectedDriver.lng }}
            onCloseClick={() => setSelectedDriver(null)}
          >
            <div className="text-gray-900 p-1">
              <p className="font-semibold">{selectedDriver.businessName || selectedDriver.name || 'Driver'}</p>
              <p className="text-xs">ID: {selectedDriver.id.slice(-6)}</p>
              <p className="text-xs">Status: {selectedDriver.status || 'Available'}</p>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
};
